// material-ui
import {
  Box,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Typography,
  useTheme,
  CircularProgress,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  styled,
  Tabs,
  Tab,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Stack,
} from '@mui/material';
import { useState, useEffect, useCallback, Fragment } from 'react';
import { DateRangePicker } from 'rsuite';
import 'rsuite/dist/rsuite.min.css';
import moment from 'moment';
import { visuallyHidden } from '@mui/utils';

// project imports
import AccountBalanceWalletTwoToneIcon from '@mui/icons-material/AccountBalanceWalletTwoTone';
import AssignmentTwoToneIcon from '@mui/icons-material/AssignmentTwoTone';
import PeopleTwoToneIcon from '@mui/icons-material/PeopleTwoTone';
import ErrorOutlineTwoToneIcon from '@mui/icons-material/ErrorOutlineTwoTone';
import { ReactComponent as CloseIcon } from 'assets/images/svg/close.svg';
import { gridSpacing } from 'store/constant';
import SideIconCard from 'ui-component/cards/SideIconCard';
import getAxiosInstance from 'api';
import { getDateRangeFromQuickOption } from 'utils/common';
import toast from 'react-hot-toast';
import DialogCancelButton from 'ui-component/extended/DialogCancelButton';

const StyledTableCell = styled(TableCell)(() => ({
  backgroundColor: '#FFF',
  border: '1px solid #DEDEDE',
  color: '#212120',
  fontSize: 14,
  fontWeight: 500,
}));

const StyledTableCellHead = styled(TableCell)(() => ({
  backgroundColor: '#F0F0F0',
  color: 'rgba(33,33,32,0.6)',
  border: '1px solid #DEDEDE',
  fontWeight: 600,
}));

const StyledCard = styled(Card)(() => ({
  padding: '24px',
  boxShadow: '0px 2px 6px rgba(0,0,0,0.06)',
  borderRadius: '12px',
  border: '1px solid rgba(0,0,0,0.1)',
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.secondary.main,
    height: 2,
  },
  '& .MuiTab-root': {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: 16,
    minHeight: 48,
    color: 'rgba(33, 33, 32, 0.6)',
    '&.Mui-selected': {
      color: theme.palette.secondary.main,
      fontWeight: 600,
    },
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  },
}));

const TitleTypography = styled(Typography)(() => ({
  fontWeight: 400,
  fontSize: 16,
  lineHeight: '24px',
  color: 'rgba(33, 33, 32, 0.6)',
}));

const ContentTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 400,
  fontSize: 16,
  lineHeight: '24px',
  color: theme.palette.common.black,
  wordBreak: 'break-word',
}));

const DetailsContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#F9F9F9',
  borderRadius: '12px',
  border: '1px solid #DEDEDE',
  padding: theme.spacing(2),
}));

const quickRanges = [
  { label: 'Current Day', value: 'day' },
  { label: 'Current Week', value: 'week' },
  { label: 'Current Month', value: 'month' },
  { label: 'Last 3 Months', value: '3months' },
  { label: 'Last 6 Months', value: '6months' },
  { label: 'Last 12 Months', value: '12months' },
  { label: 'Quarter to date', value: 'quarter' },
  { label: 'All Time', value: 'all' },
];

// Error Code Mapping
const CustomErrorCodes = {
  ValidationError: 412,
  PaymentError: 451,
  DocumentUploadError: 452,
  BOIIntegrationError: 453,
  InternalServerError: 500,
  BadRequestError: 400,
  NotFoundError: 404,
};

// Map errorCode to Error Type based on CustomErrorCodes enum
const getErrorType = (errorCode, responseStatus) => {
  // Create reverse mapping from errorCode enum name to display name
  const errorCodeToTypeMap = {
    // String enum names
    'ValidationError': 'Validation Error',
    'PaymentError': 'Payment Failed',
    'DocumentUploadError': 'Document Upload Failed',
    'BOIIntegrationError': 'API Timeout',
    'InternalServerError': 'Internal Server Error',
    'BadRequestError': 'Bad Request',
    'NotFoundError': 'Not Found',
    // Numeric codes
    [CustomErrorCodes.ValidationError]: 'Validation Error',
    [CustomErrorCodes.PaymentError]: 'Payment Failed',
    [CustomErrorCodes.DocumentUploadError]: 'Document Upload Failed',
    [CustomErrorCodes.BOIIntegrationError]: 'API Timeout',
    [CustomErrorCodes.InternalServerError]: 'Internal Server Error',
    [CustomErrorCodes.BadRequestError]: 'Bad Request',
    [CustomErrorCodes.NotFoundError]: 'Not Found',
  };

  // First, try to use errorCode if it exists and is not null
  if (errorCode !== null && errorCode !== undefined) {
    // Check if errorCode is a string enum name
    if (typeof errorCode === 'string' && errorCodeToTypeMap[errorCode]) {
      return errorCodeToTypeMap[errorCode];
    }
    // Check if errorCode is a number matching CustomErrorCodes
    if (typeof errorCode === 'number' && errorCodeToTypeMap[errorCode]) {
      return errorCodeToTypeMap[errorCode];
    }
  }
  
  // Fall back to responseStatus mapping if errorCode is null or not found
  return getErrorTypeByStatus(responseStatus);
};

// Map responseStatus to Error Type (fallback when errorCode is null)
const getErrorTypeByStatus = (responseStatus) => {
  switch (responseStatus) {
    case CustomErrorCodes.ValidationError:
      return 'Validation Error';
    case CustomErrorCodes.PaymentError:
      return 'Payment Failed';
    case CustomErrorCodes.DocumentUploadError:
      return 'Document Upload Failed';
    case CustomErrorCodes.BOIIntegrationError:
      return 'API Timeout';
    case CustomErrorCodes.InternalServerError:
      return 'Internal Server Error';
    case CustomErrorCodes.BadRequestError:
      return 'Bad Request';
    case CustomErrorCodes.NotFoundError:
      return 'Not Found';
    default:
      return 'Unknown Error';
  }
};

// ==============================|| SMEDAN DASHBOARD ||============================== //

const Smedan = () => {
  const theme = useTheme();
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedDate, setSelectedDate] = useState([
    moment().startOf('month').toDate(),
    moment().toDate(),
  ]);
  const [quickRangeValue, setQuickRangeValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    totalPayment: 0,
    totalApplications: 0,
    approvedApplications: 0,
    errorCounts: 0,
  });

  // Overview tab state
  const [revenueData, setRevenueData] = useState([]);
  const [revenueLoading, setRevenueLoading] = useState(false);

  // Payments tab state
  const [paymentRows, setPaymentRows] = useState([]);
  const [paymentTotalRows, setPaymentTotalRows] = useState(0);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentPage, setPaymentPage] = useState(0);
  const [paymentLimit, setPaymentLimit] = useState(10);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paymentDetailOpen, setPaymentDetailOpen] = useState(false);

  // Applications tab state
  const [applicationData, setApplicationData] = useState(null);
  const [applicationLoading, setApplicationLoading] = useState(false);

  // Errors tab state
  const [errorRows, setErrorRows] = useState([]);
  const [errorTotalRows, setErrorTotalRows] = useState(0);
  const [errorLoading, setErrorLoading] = useState(false);
  const [errorPage, setErrorPage] = useState(0);
  const [errorLimit, setErrorLimit] = useState(10);
  const [errorOrder, setErrorOrder] = useState('DESC');
  const [errorOrderBy, setErrorOrderBy] = useState('createdAt');

  const fetchSmedanData = useCallback(() => {
    setLoading(true);
    getAxiosInstance(
      'adminOrchestrator/dashboard/getSmedanApplicationInsights',
      'post',
      {
        startDate: moment(selectedDate[0]).format('YYYY-MM-DD'),
        endDate: moment(selectedDate[1]).format('YYYY-MM-DD'),
      },
      { 'Content-Type': 'application/json', 'journey-type': 'Dashboard' }
    )
      .then((res) => {
        if (res?.data?.codes === 200 && res?.data?.data) {
          setData({
            totalPayment: res.data.data.totalPayment || 0,
            totalApplications: res.data.data.totalApplications || 0,
            approvedApplications: res.data.data.approvedApplications || 0,
            errorCounts: res.data.data.errorCounts || 0,
          });
        } else {
          toast.error(res?.data?.message || 'Failed to load data');
          setData({
            totalPayment: 0,
            totalApplications: 0,
            approvedApplications: 0,
            errorCounts: 0,
          });
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || 'Error fetching data');
        setData({
          totalPayment: 0,
          totalApplications: 0,
          approvedApplications: 0,
          errorCounts: 0,
        });
      })
      .finally(() => setLoading(false));
  }, [selectedDate]);

  // Fetch Revenue by Service (Overview tab)
  const fetchRevenueData = useCallback(() => {
    setRevenueLoading(true);
    getAxiosInstance(
      'adminOrchestrator/dashboard/getServiceRevenue',
      'post',
      {
        startDate: moment(selectedDate[0]).format('YYYY-MM-DD'),
        endDate: moment(selectedDate[1]).format('YYYY-MM-DD'),
      },
      { 'Content-Type': 'application/json', 'journey-type': 'Dashboard' }
    )
      .then((res) => {
        if (res?.data?.codes === 200 && res?.data?.data) {
          const revenueList = Array.isArray(res.data.data) ? res.data.data : [];
          // Calculate total revenue for percentage calculation
          const totalRevenue = revenueList.reduce(
            (sum, item) => sum + parseFloat(item.totalPrice || 0),
            0
          );
          // Add count and percentage
          const formattedData = revenueList.map((item) => {
            const count = Math.floor(
              parseFloat(item.totalPrice || 0) / parseFloat(item.averagePrice || 1)
            );
            const percentage =
              totalRevenue > 0
                ? ((parseFloat(item.totalPrice || 0) / totalRevenue) * 100).toFixed(1)
                : 0;
            return {
              ...item,
              count,
              percentage: parseFloat(percentage),
            };
          });
          setRevenueData(formattedData);
        } else {
          setRevenueData([]);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || 'Error fetching revenue data');
        setRevenueData([]);
      })
      .finally(() => setRevenueLoading(false));
  }, [selectedDate]);

  // Fetch Payment Details (Payments tab)
  const fetchPaymentData = useCallback(() => {
    setPaymentLoading(true);
    getAxiosInstance(
      'adminOrchestrator/dashboard/getPaymentDetailsList',
      'post',
      {
        startDate: moment(selectedDate[0]).format('YYYY-MM-DD'),
        endDate: moment(selectedDate[1]).format('YYYY-MM-DD'),
        page: paymentPage + 1,
        limit: paymentLimit,
      },
      { 'Content-Type': 'application/json', 'journey-type': 'Dashboard' }
    )
      .then((res) => {
        console.log('Payment API Response:', res.data);
        if (res?.data?.codes === 200) {
          // Handle different response structures
          let paymentList = [];
          let total = 0;

          if (Array.isArray(res.data.data)) {
            // Direct array response (as per user's API example)
            paymentList = res.data.data;
            total = res.data.data.length;
          } else if (res.data.data?.items) {
            // Paginated response with items
            paymentList = res.data.data.items || [];
            total = res.data.data.total || paymentList.length;
          } else if (res.data.data?.data && Array.isArray(res.data.data.data)) {
            // Nested data structure
            paymentList = res.data.data.data;
            total = res.data.data.total || paymentList.length;
          }

          console.log('Processed Payment List:', paymentList);
          setPaymentRows(paymentList);
          setPaymentTotalRows(total);
        } else {
          console.warn('Payment API returned non-200 code:', res?.data?.codes);
          setPaymentRows([]);
          setPaymentTotalRows(0);
        }
      })
      .catch((err) => {
        console.error('Payment data fetch error:', err);
        console.error('Error response:', err?.response?.data);
        toast.error(err?.response?.data?.message || 'Error fetching payment data');
        setPaymentRows([]);
        setPaymentTotalRows(0);
      })
      .finally(() => setPaymentLoading(false));
  }, [selectedDate, paymentPage, paymentLimit]);

  // Fetch Application Status Count (Applications tab)
  const fetchApplicationData = useCallback(() => {
    setApplicationLoading(true);
    getAxiosInstance(
      'adminOrchestrator/dashboard/getApplicationStatusCount',
      'post',
      {
        startDate: moment(selectedDate[0]).format('YYYY-MM-DD'),
        endDate: moment(selectedDate[1]).format('YYYY-MM-DD'),
      },
      { 'Content-Type': 'application/json', 'journey-type': 'Dashboard' }
    )
      .then((res) => {
        if (res?.data?.codes === 200 && res?.data?.data) {
          setApplicationData(res.data.data);
        } else {
          setApplicationData(null);
        }
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message || 'Error fetching application data'
        );
        setApplicationData(null);
      })
      .finally(() => setApplicationLoading(false));
  }, [selectedDate]);

  // Fetch Errors data (Errors tab)
  const fetchErrorsData = useCallback(() => {
    setErrorLoading(true);
    getAxiosInstance(
      'adminOrchestrator/dashboard/getErrorDetailsList',
      'post',
      {
        startDate: moment(selectedDate[0]).format('YYYY-MM-DD'),
        endDate: moment(selectedDate[1]).format('YYYY-MM-DD'),
        page: errorPage + 1,
        limit: errorLimit,
        sortBy: errorOrderBy,
        sortDirection: errorOrder,
      },
      { 'Content-Type': 'application/json', 'journey-type': 'Dashboard' }
    )
      .then((res) => {
        if (res?.data?.codes === 200 && res?.data?.data) {
          const errorList = res.data.data.items || [];
          const total = res.data.data.total || 0;
          
          // Map API response to table format
          const mappedErrors = errorList.map((item) => ({
            id: item.id,
            errorType: getErrorType(item.errorCode, item.responseStatus),
            user: item.userName || '-',
            section: item.section || '-',
            message: item.message || '-',
            createdAt: item.createdAt,
          }));

          setErrorRows(mappedErrors);
          setErrorTotalRows(total);
        } else {
          setErrorRows([]);
          setErrorTotalRows(0);
        }
      })
      .catch((err) => {
        console.error('Error data fetch error:', err);
        toast.error(err?.response?.data?.message || 'Error fetching error data');
        setErrorRows([]);
        setErrorTotalRows(0);
      })
      .finally(() => setErrorLoading(false));
  }, [selectedDate, errorPage, errorLimit, errorOrder, errorOrderBy]);

  useEffect(() => {
    fetchSmedanData();
  }, [fetchSmedanData]);

  useEffect(() => {
    if (selectedTab === 0) {
      // Overview tab
      fetchRevenueData();
    } else if (selectedTab === 1) {
      // Payments tab
      fetchPaymentData();
    } else if (selectedTab === 2) {
      // Applications tab
      fetchApplicationData();
    } else if (selectedTab === 3) {
      // Errors tab
      fetchErrorsData();
    }
  }, [
    selectedTab,
    fetchRevenueData,
    fetchPaymentData,
    fetchApplicationData,
    fetchErrorsData,
  ]);

  const handleRequestSort = (_, property) => {
    const isAsc = errorOrderBy === property && errorOrder === 'ASC';
    setErrorOrder(isAsc ? 'DESC' : 'ASC');
    setErrorOrderBy(property);
    setErrorPage(0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-NG').format(num);
  };

  const formatPaymentId = (row, idx) =>
    `PAY-${moment(row.createdAt || new Date()).format('YYYY')}-${String(
      row.id || idx + 1,
    ).padStart(3, '0')}`;

  const handlePaymentIdClick = (row) => {
    setSelectedPayment(row);
    setPaymentDetailOpen(true);
  };

  const handlePaymentDetailClose = () => {
    setPaymentDetailOpen(false);
    setSelectedPayment(null);
  };

  // Overview Tab Content - Revenue by Service
  const renderOverviewTab = () => {
    const revenueHeadCells = [
      { id: 'serviceName', label: 'Service' },
      { id: 'count', label: 'Count' },
      { id: 'totalPrice', label: 'Total Revenue' },
      { id: 'averagePrice', label: 'Avg. Amount' },
      { id: 'percentage', label: '% of Total' },
    ];

    return (
      <>
        {/* Metric Cards */}
        {loading ? (
          <Grid item xs={12}>
            <Box display="flex" justifyContent="center" py={5}>
              <CircularProgress color="secondary" />
            </Box>
          </Grid>
        ) : (
          <>
            <Grid item xs={12} lg={3} sm={6}>
              <SideIconCard
                primary={formatCurrency(data.totalPayment)}
                secondary="Total Revenue"
                secondarySub={`${data.totalApplications} applications`}
                percentage="+0%"
                color={theme.palette.success.main}
                iconPrimary={AccountBalanceWalletTwoToneIcon}
              />
            </Grid>
            <Grid item xs={12} lg={3} sm={6}>
              <SideIconCard
                primary={data.totalApplications}
                secondary="Applications Started"
                percentage="+0%"
                color={theme.palette.primary.main}
                iconPrimary={AssignmentTwoToneIcon}
              />
            </Grid>
            <Grid item xs={12} lg={3} sm={6}>
              <SideIconCard
                primary={data.approvedApplications}
                secondary="Applications Approved"
                percentage="+0%"
                color={theme.palette.secondary.main}
                iconPrimary={PeopleTwoToneIcon}
              />
            </Grid>
            <Grid item xs={12} lg={3} sm={6}>
              <SideIconCard
                primary={data.errorCounts}
                secondary="Errors & Failed Actions"
                secondarySub={`${data.errorCounts} errors`}
                color={theme.palette.error.main}
                iconPrimary={ErrorOutlineTwoToneIcon}
              />
            </Grid>
          </>
        )}

        {/* Revenue by Service Table */}
        <Grid item xs={12}>
          <StyledCard>
            <Typography variant="h3" sx={{ fontWeight: 600, mb: 3, color: '#212120' }}>
              Revenue by Service
            </Typography>
            {revenueLoading ? (
              <Box display="flex" justifyContent="center" py={5}>
                <CircularProgress color="secondary" />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {revenueHeadCells.map((cell) => (
                        <StyledTableCellHead key={cell.id}>
                          {cell.label}
                        </StyledTableCellHead>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {revenueData.length > 0 ? (
                      revenueData.map((row, idx) => (
                        <TableRow hover key={idx}>
                          <StyledTableCell>{row.serviceName}</StyledTableCell>
                          <StyledTableCell>{formatNumber(row.count)}</StyledTableCell>
                          <StyledTableCell>
                            {formatCurrency(parseFloat(row.totalPrice || 0))}
                          </StyledTableCell>
                          <StyledTableCell>
                            {formatCurrency(parseFloat(row.averagePrice || 0))}
                          </StyledTableCell>
                          <StyledTableCell>{row.percentage.toFixed(1)}%</StyledTableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={revenueHeadCells.length} align="center">
                          No records found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </StyledCard>
        </Grid>
      </>
    );
  };

  // Payments Tab Content - Recent Payments
  const renderPaymentsTab = () => {
    const paymentHeadCells = [
      { id: 'paymentId', label: 'Payment ID' },
      { id: 'applicant', label: 'Applicant' },
      { id: 'services', label: 'Services' },
      { id: 'amount', label: 'Amount' },
      { id: 'status', label: 'Status' },
      { id: 'time', label: 'Time' },
    ];

    return (
      <Grid item xs={12}>
        <StyledCard>
          <Typography
            variant="h3"
            sx={{ fontWeight: 600, mb: 3, color: '#212120' }}
          >
            Recent Payments
          </Typography>
          {paymentLoading ? (
            <Box display="flex" justifyContent="center" py={5}>
              <CircularProgress color="secondary" />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {paymentHeadCells.map((cell) => (
                        <StyledTableCellHead key={cell.id}>
                          {cell.label}
                        </StyledTableCellHead>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paymentRows.length > 0 ? (
                      paymentRows.map((row, idx) => (
                        <TableRow hover key={row.id || idx}>
                          <StyledTableCell>
                            <Typography
                              component="button"
                              type="button"
                              onClick={() => handlePaymentIdClick(row)}
                              sx={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                cursor: 'pointer',
                                color: theme.palette.secondary.main,
                                fontWeight: 600,
                                fontSize: 14,
                                textAlign: 'left',
                                '&:hover': {
                                  textDecoration: 'underline',
                                },
                              }}
                            >
                              {formatPaymentId(row, idx)}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell>
                            {row.userName || '-'}
                          </StyledTableCell>
                          <StyledTableCell>
                            {Array.isArray(row.services)
                              ? row.services.join(', ')
                              : row.services || '-'}
                          </StyledTableCell>
                          <StyledTableCell>
                            {formatCurrency(parseFloat(row.amount || 0))}
                          </StyledTableCell>
                          <StyledTableCell>
                            <Chip
                              label={row.paymentStatus || 'Success'}
                              size="small"
                              sx={{
                                backgroundColor:
                                  (row.paymentStatus || 'Success') === 'Success'
                                    ? 'rgba(76, 175, 80, 0.1)'
                                    : 'rgba(244, 67, 54, 0.1)',
                                color:
                                  (row.paymentStatus || 'Success') === 'Success'
                                    ? '#4CAF50'
                                    : '#F44336',
                                fontWeight: 500,
                              }}
                            />
                          </StyledTableCell>
                          <StyledTableCell>
                            {row.createdAt
                              ? moment(row.createdAt).format('HH:mm')
                              : '-'}
                          </StyledTableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={paymentHeadCells.length}
                          align="center"
                        >
                          No records found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={paymentTotalRows}
                page={paymentPage}
                rowsPerPage={paymentLimit}
                onPageChange={(_, newPage) => setPaymentPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setPaymentLimit(parseInt(e.target.value, 10));
                  setPaymentPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25]}
              />
            </>
          )}
        </StyledCard>
      </Grid>
    );
  };

  // Applications Tab Content - Application Funnel
  const renderApplicationsTab = () => {
    if (!applicationData) {
      return (
        <Grid item xs={12}>
          <StyledCard>
            <Typography variant="h3" sx={{ fontWeight: 600, mb: 3, color: '#212120' }}>
              Application Funnel
            </Typography>
            {applicationLoading ? (
              <Box display="flex" justifyContent="center" py={5}>
                <CircularProgress color="secondary" />
              </Box>
            ) : (
              <Box py={5} textAlign="center">
                <Typography>No data available</Typography>
              </Box>
            )}
          </StyledCard>
        </Grid>
      );
    }

    const total = applicationData.totalApplications || 0;
    const stages = [
      {
        label: 'Account Created',
        count: total,
        percentage: 100,
      },
      {
        label: 'Company Info Completed',
        count: applicationData.companyInfoCompleted || 0,
        percentage: total > 0 ? ((applicationData.companyInfoCompleted || 0) / total) * 100 : 0,
      },
      {
        label: 'Identity Verified',
        count: applicationData.ninValidationCompleted || 0,
        percentage: total > 0 ? ((applicationData.ninValidationCompleted || 0) / total) * 100 : 0,
      },
      {
        label: 'Financial Info Submitted',
        count: applicationData.financialStatementCompleted || 0,
        percentage: total > 0 ? ((applicationData.financialStatementCompleted || 0) / total) * 100 : 0,
      },
      {
        label: 'Business Plan Uploaded',
        count: applicationData.businessPlanCompleted || 0,
        percentage: total > 0 ? ((applicationData.businessPlanCompleted || 0) / total) * 100 : 0,
      },
      {
        label: 'Application Submitted',
        count: applicationData.customerDeclarationCompleted || 0,
        percentage: total > 0 ? ((applicationData.customerDeclarationCompleted || 0) / total) * 100 : 0,
      },
      {
        label: 'Payment Completed',
        count: applicationData.paymentCompleted || 0,
        percentage: total > 0 ? ((applicationData.paymentCompleted || 0) / total) * 100 : 0,
      }
    ];

    return (
      <Grid item xs={12}>
        <StyledCard>
          <Typography variant="h3" sx={{ fontWeight: 600, mb: 3, color: '#212120' }}>
            Application Funnel
          </Typography>
          {applicationLoading ? (
            <Box display="flex" justifyContent="center" py={5}>
              <CircularProgress color="secondary" />
            </Box>
          ) : (
            <Box>
              {stages.map((stage, idx) => (
                <Box key={idx} sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1,
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {stage.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: 'rgba(33, 33, 32, 0.6)' }}
                    >
                      {formatNumber(stage.count)} ({stage.percentage.toFixed(0)}%)
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      height: 8,
                      backgroundColor: 'rgba(0, 0, 0, 0.05)',
                      borderRadius: 1,
                      overflow: 'hidden',
                      position: 'relative',
                    }}
                  >
                    <Box
                      sx={{
                        width: `${stage.percentage}%`,
                        height: '100%',
                        backgroundColor: theme.palette.primary.main,
                        transition: 'width 0.3s ease',
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </StyledCard>
      </Grid>
    );
  };

  // Payment Detail Dialog
  const renderPaymentDetailDialog = () => {
    if (!selectedPayment) return null;

    const serviceBreakdown = Array.isArray(selectedPayment.serviceBreakdown)
      ? selectedPayment.serviceBreakdown
      : Array.isArray(selectedPayment.services)
        ? selectedPayment.services.map((name) => ({
            serviceName: name,
            price: null,
            quantity: null,
            lineTotal: null,
          }))
        : [];

    const detailRows = [
      {
        label: 'Payment ID:',
        value: formatPaymentId(selectedPayment, 0),
      },
      {
        label: "Sender's Name:",
        value: selectedPayment.originatorName || '-',
      },
      {
        label: 'Amount:',
        value: formatCurrency(parseFloat(selectedPayment.amount || 0)),
      },
      {
        label: 'Payment Date & Time:',
        value: selectedPayment.createdAt
          ? moment(selectedPayment.createdAt).format('MMM D, YYYY HH:mm')
          : '-',
      },
      {
        label: 'Payment Status:',
        value: selectedPayment.paymentStatus || 'Success',
        isStatus: true,
      },
      {
        label: 'Session ID:',
        value: selectedPayment.sessionId || '-',
      },
    ];

    const paymentStatus = selectedPayment.paymentStatus || 'Success';

    return (
      <Dialog
        open={paymentDetailOpen}
        onClose={handlePaymentDetailClose}
        sx={{
          '& .MuiDialog-container': {
            '& .MuiPaper-root': {
              width: '100%',
              maxWidth: '580px',
            },
          },
        }}
      >
        <Stack px={2} my={2}>
          <Typography variant="h4" fontWeight={500}>
            Payment Details
          </Typography>
          <IconButton
            sx={{ position: 'absolute', right: 5, top: 5 }}
            onClick={handlePaymentDetailClose}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
        <Divider />
        <DialogContent>
          <Grid container spacing={2}>
            {detailRows.map((item) => (
              <Fragment key={item.label}>
                <Grid item xs={6}>
                  <TitleTypography>{item.label}</TitleTypography>
                </Grid>
                <Grid item xs={6}>
                  {item.isStatus ? (
                    <Chip
                      label={paymentStatus}
                      size="small"
                      sx={{
                        backgroundColor:
                          paymentStatus === 'Success'
                            ? 'rgba(76, 175, 80, 0.1)'
                            : 'rgba(244, 67, 54, 0.1)',
                        color:
                          paymentStatus === 'Success' ? '#4CAF50' : '#F44336',
                        fontWeight: 500,
                      }}
                    />
                  ) : (
                    <ContentTypography>{item.value}</ContentTypography>
                  )}
                </Grid>
              </Fragment>
            ))}
          </Grid>

          <Box mt={3}>
            <Typography variant="h4" fontWeight={400}>
              Payment Breakdown
            </Typography>
            <DetailsContainer sx={{ mt: 2 }}>
              {serviceBreakdown.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <StyledTableCellHead>Service</StyledTableCellHead>
                        <StyledTableCellHead align="right">
                          Qty
                        </StyledTableCellHead>
                        <StyledTableCellHead align="right">
                          Price
                        </StyledTableCellHead>
                        <StyledTableCellHead align="right">
                          Total
                        </StyledTableCellHead>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {serviceBreakdown.map((item, index) => (
                        <TableRow key={`${item.serviceName}-${index}`}>
                          <StyledTableCell>{item.serviceName}</StyledTableCell>
                          <StyledTableCell align="right">
                            {item.quantity ?? '-'}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {item.price != null
                              ? formatCurrency(parseFloat(item.price))
                              : '-'}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {item.lineTotal != null
                              ? formatCurrency(parseFloat(item.lineTotal))
                              : '-'}
                          </StyledTableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <ContentTypography>-</ContentTypography>
              )}
            </DetailsContainer>
          </Box>

          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="flex-end"
            gap={1.5}
            mt={3}
          >
            <DialogCancelButton onClick={handlePaymentDetailClose}>
              Close
            </DialogCancelButton>
          </Stack>
        </DialogContent>
      </Dialog>
    );
  };

  // Errors Tab Content - Errors & Failed Actions
  const renderErrorsTab = () => {
    const errorHeadCells = [
      { id: 'errorType', label: 'Error Type', disableSort: true },
      { id: 'user', label: 'User', disableSort: true },
      { id: 'section', label: 'Section', disableSort: true },
      { id: 'message', label: 'Message', disableSort: true },
      { id: 'createdAt', label: 'Created At' },
    ];

    return (
      <Grid item xs={12}>
        <StyledCard>
          <Typography variant="h3" sx={{ fontWeight: 600, mb: 3, color: '#212120' }}>
            Errors & Failed Actions
          </Typography>
          {errorLoading ? (
            <Box display="flex" justifyContent="center" py={5}>
              <CircularProgress color="secondary" />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {errorHeadCells.map((cell) => (
                        <StyledTableCellHead
                          key={cell.id}
                          sortDirection={
                            !cell.disableSort && errorOrderBy === cell.id
                              ? errorOrder
                              : false
                          }
                        >
                          {cell.disableSort ? (
                            cell.label
                          ) : (
                            <TableSortLabel
                              active={errorOrderBy === cell.id}
                              direction={
                                errorOrderBy === cell.id
                                  ? errorOrder.toLowerCase()
                                  : 'asc'
                              }
                              onClick={(e) => handleRequestSort(e, cell.id)}
                            >
                              {cell.label}
                              {errorOrderBy === cell.id && (
                                <Box component="span" sx={visuallyHidden}>
                                  {errorOrder === 'DESC'
                                    ? 'sorted descending'
                                    : 'sorted ascending'}
                                </Box>
                              )}
                            </TableSortLabel>
                          )}
                        </StyledTableCellHead>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {errorRows.length > 0 ? (
                      errorRows.map((row, idx) => (
                        <TableRow hover key={row.id || idx}>
                          <StyledTableCell>{row.errorType}</StyledTableCell>
                          <StyledTableCell>{row.user}</StyledTableCell>
                          <StyledTableCell>{row.section}</StyledTableCell>
                          <StyledTableCell>{row.message}</StyledTableCell>
                          <StyledTableCell>
                            {row.createdAt
                              ? moment(row.createdAt).format('MMM D, YYYY HH:mm')
                              : '-'}
                          </StyledTableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={errorHeadCells.length} align="center">
                          No records found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={errorTotalRows}
                page={errorPage}
                rowsPerPage={errorLimit}
                onPageChange={(_, newPage) => setErrorPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setErrorLimit(parseInt(e.target.value, 10));
                  setErrorPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25]}
              />
            </>
          )}
        </StyledCard>
      </Grid>
    );
  };

  return (
    <Grid container spacing={gridSpacing}>
      <Grid item xs={12}>
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Box>
            <Typography variant="h2" sx={{ fontWeight: 600, mb: 0.5 }}>
              e-Doc Admin Dashboard
            </Typography>
            <Typography
              variant="h4"
              sx={{ color: 'text.secondary', fontWeight: 400 }}
            >
              SMEDAN Analytics & Monitoring
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <Select
                value={quickRangeValue}
                onChange={(e) => {
                  const value = e.target.value;
                  setQuickRangeValue(value);
                  if (value) {
                    const range = getDateRangeFromQuickOption(value);
                    if (range) {
                      setSelectedDate([range.start, range.end]);
                    }
                  }
                }}
                displayEmpty
                sx={{
                  backgroundColor: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                  },
                }}
              >
                <MenuItem value="">Quick Range</MenuItem>
                {quickRanges.map((range) => (
                  <MenuItem key={range.value} value={range.value}>
                    {range.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <DateRangePicker
              appearance="default"
              placeholder="Select Date Range"
              value={selectedDate}
              onChange={(value) => {
                if (value && value.length === 2) {
                  setSelectedDate([value[0], value[1]]);
                }
                setQuickRangeValue('');
              }}
              showOneCalendar
            />
          </Box>
        </Box>
      </Grid>

      {/* Tabs Section */}
      <Grid item xs={12}>
        <StyledCard sx={{ p: 0 }}>
          <Box sx={{ px: 3, pt: 2 }}>
            <StyledTabs
              value={selectedTab}
              onChange={(_, newValue) => setSelectedTab(newValue)}
            >
              <Tab label="Overview" />
              <Tab label="Payments" />
              <Tab label="Applications" />
              <Tab label="Errors" />
            </StyledTabs>
          </Box>
          <Box sx={{ px: 3, pb: 3, pt: 3 }}>
            <Grid container spacing={gridSpacing}>
              {selectedTab === 0 && renderOverviewTab()}
              {selectedTab === 1 && renderPaymentsTab()}
              {selectedTab === 2 && renderApplicationsTab()}
              {selectedTab === 3 && renderErrorsTab()}
            </Grid>
          </Box>
        </StyledCard>
      </Grid>
      {renderPaymentDetailDialog()}
    </Grid>
  );
};

export default Smedan;
